import {
  ALIASES,
  expectedRegion,
  isBareRegion,
  segmentsOf,
  trailingRegion,
  withinRegion,
} from './RegionCheck';

/**
 * Version of the query-building rules. v0.1.0 (single as-is query, first result)
 * is version 1; PR #27 was version 2; P-15b was 3; P-15c (success cache versioned) was 4;
 * P-15d (full region table, historical localities) was 5; no broader query for keys
 * naming no region is 6.
 * Bump whenever buildQueries, selectAcceptable, ALIASES, or RegionCheck changes —
 * GeoCache keys both its result and failure caches on it, so a bump retries every
 * known failure and re-looks-up every saved non-seed result once.
 */
export const QUERY_BUILDER_VERSION = 6;

export type QueryStage =
  | 'as-is'
  | 'cleaned'
  | 'locality+region'
  | 'locality+county'
  | 'town+region'
  | 'broader';

export interface PlaceQuery {
  query: string;
  /** True when a hit would not pinpoint the named locality (broader or bare region). */
  approximate: boolean;
  stage: QueryStage;
}

/** The subset of a Nominatim `format=jsonv2` result the acceptance rule reads. */
export interface NominatimResult {
  lat: string;
  lon: string;
  category?: string;
}

export interface Selection {
  coords: { lat: number; lng: number } | null;
  /** Results skipped because they are not a place/boundary (streets, shops, offices…). */
  rejectedCategory: number;
  /** Results skipped because they fall outside the region the place string names. */
  rejectedRegion: number;
}

const ACCEPTED_CATEGORIES = new Set(['place', 'boundary']);
const COUNTRY_SUFFIXES = new Set(['united states', 'usa', 'united states of america', 'canada']);

/** Uncertainty and filler words before a place: "prob. x", "of, x", "hamlet of x". */
const QUALIFIER_PREFIX =
  /^(?:prob\.?|probably|poss\.?|possibly|near|abt\.?|of|hamlet of|parish of|town of)\s+/;
const QUALIFIER_ONLY = /^(?:prob\.?|probably|poss\.?|possibly|near|abt\.?|of)$/;

/**
 * Localities renamed since the record was made. WikiTree records a place by its name at
 * the time; Nominatim knows today's name. Rewritten in queries only — the label keeps
 * the historical name. Add from owner review or audit output, not guesses.
 */
export const HISTORICAL_LOCALITIES: Record<string, string> = {
  monomoit: 'chatham', // Chatham, Barnstable County, Massachusetts — renamed 1712
};

function rewriteAlias(segment: string): string {
  const alias = ALIASES[segment];
  return alias !== undefined && alias.rewrite ? alias.region : segment;
}

function cleanSegment(segment: string): string {
  const stripped = rewriteAlias(segment)
    .replace(QUALIFIER_PREFIX, '')
    .replace(/^colony of /, '')
    .replace(/ (?:colony|township|parish|village)$/, '')
    .replace(/^amt /, '')
    .replace(/(\S) co\.?$/, '$1 county') // "barnstable co." → "barnstable county"
    .replace(/^([a-z]{2})\.$/, '$1') // "ma." → "ma"
    .trim();
  if (QUALIFIER_ONLY.test(stripped)) {
    return '';
  }
  return HISTORICAL_LOCALITIES[stripped] ?? rewriteAlias(stripped);
}

function cleanedSegments(normalized: string): string[] {
  const segments = segmentsOf(normalized)
    .map(cleanSegment)
    .filter((s) => s.length > 0);
  // Drop trailing country names, and a trailing "new england" when an earlier segment
  // already names the (more specific) state — Nominatim matches neither as a component.
  while (segments.length > 1) {
    const last = segments[segments.length - 1]!;
    const stateBefore = last === 'new england' && segments.slice(0, -1).some(isBareRegion);
    if (!COUNTRY_SUFFIXES.has(last) && !stateBefore) {
      break;
    }
    segments.pop();
  }
  // "new london ct" → ["new london", "connecticut"]. When no segment names a region on
  // its own, split a trailing region word off the last segment: as one string,
  // Nominatim matches same-named streets ("New London Ct") instead of the town.
  const last = segments[segments.length - 1];
  if (last !== undefined && !segments.some((s) => isBareRegion(s))) {
    const split = trailingRegion(last);
    if (split !== null) {
      segments.splice(-1, 1, cleanSegment(split.locality) || split.locality, split.region);
    }
  }
  return segments;
}

/**
 * Ordered, de-duplicated Nominatim query variants for a normalized place key:
 * as-is → cleaned → locality + region → locality + county → town + region
 * (approximate) → broader (approximate).
 *
 * "locality + county" drops the state, for colonial places filed under a colony whose
 * borders differ from today's state ("wells, york, massachusetts bay" is in Maine).
 * "town + region" drops the first segment and the county, for keys that lead with a
 * parish or church and name a historical county ("st mary, lambeth, surrey, england").
 * "broader" is skipped when the key names no region: nothing can check its result, and
 * dropping the locality matched the wrong continent ("chatham, plymouth" → Plymouth,
 * England). Such a key is reported not found instead.
 */
export function buildQueries(normalized: string): PlaceQuery[] {
  const segments = cleanedSegments(normalized);
  const first = segments[0];
  const second = segments[1];
  const last = segments[segments.length - 1];
  const candidates: Array<{ query: string; stage: QueryStage; approximate: boolean }> = [
    { query: normalized, stage: 'as-is', approximate: false },
  ];

  if (segments.length > 0) {
    candidates.push({ query: segments.join(', '), stage: 'cleaned', approximate: false });
  }
  if (segments.length >= 3) {
    candidates.push({
      query: `${first!}, ${last!}`,
      stage: 'locality+region',
      approximate: false,
    });
    candidates.push({
      query: `${first!}, ${second!}`,
      stage: 'locality+county',
      approximate: false,
    });
  }
  if (segments.length >= 4) {
    candidates.push({ query: `${second!}, ${last!}`, stage: 'town+region', approximate: true });
  }
  if (segments.length >= 2 && expectedRegion(normalized) !== null) {
    const broader = segments.slice(1).join(', ');
    if (!isBareRegion(broader)) {
      candidates.push({ query: broader, stage: 'broader', approximate: true });
    }
  }

  const seen = new Set<string>();
  const result: PlaceQuery[] = [];
  for (const c of candidates) {
    if (seen.has(c.query)) {
      continue;
    }
    seen.add(c.query);
    // A whole state/country is never a precise location for the place.
    result.push({ ...c, approximate: c.approximate || isBareRegion(c.query) });
  }
  return result;
}

/**
 * First result that is a place/boundary AND not outside the region the ORIGINAL
 * key names. Shared by GeocoderService and scripts/build-seed-cache.ts so the
 * acceptance rule exists once.
 */
export function selectAcceptable(normalized: string, results: NominatimResult[]): Selection {
  let rejectedCategory = 0;
  let rejectedRegion = 0;

  for (const r of results) {
    if (r.category === undefined || !ACCEPTED_CATEGORIES.has(r.category)) {
      rejectedCategory++;
      continue;
    }
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      rejectedCategory++;
      continue;
    }
    if (withinRegion(normalized, lat, lng) === false) {
      rejectedRegion++;
      continue;
    }
    return { coords: { lat, lng }, rejectedCategory, rejectedRegion };
  }

  return { coords: null, rejectedCategory, rejectedRegion };
}
